import * as STD  from '@emersya/mpa-std-lib';

abstract class Condition {
	static deserialize(
		serialized : Condition.TSerialized,
		generateId? : () => number
	)
	{
		if (!generateId) {
			let baseId = 0;
			generateId = () => baseId++;
		}
		let condition;
		if (Condition.prototype.isScalar.call(serialized)) {
			condition =
				new Condition.ScalarCondition(<Condition.ScalarCondition.ISerialized> serialized, generateId);
		} else {
			condition =
				new Condition.AggregateCondition(
					<Condition.AggregateCondition.ISerialized> serialized,
					generateId
				);
		}
		return condition;
	};

	readonly id : number;
	readonly abstract type : string;

	isScalar() : this is Condition.ScalarCondition {
		return [
			'equal', 'notequal',
			'inferior', 'inferiorequal',
			'superior', 'superiorequal',
		].includes(this.type);
	};

	abstract evaluate(
		hostComponent : Component<any, any>,
		report : Condition.IEvaluationReportItem[]
	) : boolean;

	protected constructor(generateId : () => number) {
		this.id = generateId();
	};
};
namespace Condition {
	export interface IEvaluationReportItem {
		condition : TCondition;
		result : boolean;
		component? : Component<any, any> | null;
		property? : any;
	};

	export class AggregateCondition extends Condition {
		override readonly type : 'or' | 'and';
		readonly LHS : Condition;
		readonly RHS : Condition;

		evaluate(
			hostComponent : Component<any, any>,
			report : Condition.IEvaluationReportItem[] = []
		) : boolean {
			try {
				let result = false;
				let property : any;
				let component : Component<any, any> | null | undefined;
				result = (
					(this.type === 'and') ? (this.LHS.evaluate(hostComponent, report) && this.RHS.evaluate(hostComponent, report)) :
						(this.type === 'or') ? (this.LHS.evaluate(hostComponent, report) || this.RHS.evaluate(hostComponent, report)) :
							false
				);
				report.push({
					condition : this,
					result,
					component,
					property
				});
				return result;
			} catch (error) {
				throw STD.errors.bindFromUnknown(
					'Cannot evaluate condition!',
					{
						condition : this,
						report,
						hostComponent
					},
					error
				);
			}
		};

		constructor(
			serialized : AggregateCondition.ISerialized,
			generateId : () => number
		)
		{
			super(generateId);
			this.type = serialized.type;
			this.LHS = Condition.deserialize(serialized.LHS, generateId);
			this.RHS = Condition.deserialize(serialized.RHS, generateId);
		};
	};
	export namespace AggregateCondition {
		export interface ISerialized {
			type : AggregateCondition['type'];
			LHS : TSerialized;
			RHS : TSerialized;
		};
	}
	;

	export class ScalarCondition extends Condition {
		readonly type : (
			'equal' | 'notequal' |
			'inferior' | 'inferiorequal' |
			'superior' | 'superiorequal'
			);
		readonly component : Path.TPathRaw;
		readonly property : STD.object.TPropertyPath;
		readonly value : STD.object.JSON;

		evaluate(
			hostComponent : Component<any, any>,
			report : Condition.IEvaluationReportItem[] = []
		) : boolean {
			try {
				let result = false;
				let property : any;
				let component : Component<any, any> | null | undefined;
				component = hostComponent.component_find(
					Path.parse(this.component)
				);
				if (!component) {
					console.warn('Component not found', this);
					component = null;
				} else {
					property = this.property.evaluate(Manager.generatePropertyEvaluateVariables(
						hostComponent,
						this,
						component
					));
					if ((this.type === 'equal') || (this.type === 'notequal')) {
						result = (
							(this.type === 'equal') ? (property === this.value) :
								(property !== this.value)
						);
					} else if (
						(this.type === 'inferior') || (this.type === 'inferiorequal') ||
						(this.type === 'superior') || (this.type === 'superiorequal')
					)
					{
						const v = (typeof this.value === 'number') ? this.value : <number> {};
						result = (
							(this.type === 'inferior') ? (property < v) :
								(this.type === 'inferiorequal') ? (property <= v) :
									(this.type === 'superior') ? (property > v) :
										(property >= v)
						);
					}
				}

				report.push({
					condition : this,
					result,
					component,
					property
				});
				return result;
			} catch (e) {
				throw STD.errors.bindFromUnknown(
					'Cannot evaluate condition!',
					{
						condition : this,
						report,
						hostComponent
					},
					e
				);
			}
		};

		constructor(
			serialized : ScalarCondition.ISerialized,
			generateId : () => number
		)
		{
			super(generateId);
			this.type = serialized.type;
			this.value = serialized.value;
			this.component = serialized.component;
			this.property = STD.object.PropertyPath.create(serialized.property);
		};
	};
	export namespace ScalarCondition {
		export interface ISerialized {
			readonly type : ScalarCondition['type'];
			readonly component : Path.TPathRaw;
			readonly property : string;
			readonly value : STD.object.JSON;
		};
	}

	export type TCondition = AggregateCondition | ScalarCondition;
	export type TSerialized = AggregateCondition.ISerialized | ScalarCondition.ISerialized;
}
export default Condition;
import Component from './Component';
import Path      from './Path';
import Manager   from './Manager';
